// Reference-table lookup config for a General-Process criterion / target
// column. Rebuilt to mirror the TRANSACTION designer's Lookup editor
// (src/pages/designers/transaction/LookupEditor.tsx): a twasta Modal with a
// type-and-pick table Combobox and Display/Value column Comboboxes sourced from
// the project's real tables + columns (live preview schema ∪ Database Designer),
// rather than the free-text MUI fields the vendored gen-process app shipped.
//
// Output stays the gen-process `ColumnLookup` shape so the /genprocess/lookup
// search is unchanged. Props are unchanged, so CriteriaForm / TargetMapping
// keep calling it as-is.

import { useEffect, useMemo, useState } from 'react'
import Modal from '@/components/common/Modal'
import Combobox, { type ComboboxOption } from '@/components/common/Combobox'
import { SqlBuilderButton } from '@twasta/page-runtime/designerKit'
import { getTables, getTableInfo } from '@/api/designers'
import { livePreviewTables, livePreviewColumns } from '@/livepreview/api'
import type { ColumnLookup } from './types'
import { getGenprocProject } from './api'

export interface LookupConfigDialogProps {
  /** The thing the lookup is attached to (column name or criterion name). */
  name: string
  /** Whether this configures a target column or a criterion input. Only tweaks
   *  the explanatory copy + the {placeholder} hint. */
  subjectKind?: 'column' | 'criterion'
  value?: ColumnLookup
  onClose: () => void
  onSave: (lookup: ColumnLookup) => void
  onClear: () => void
}

interface TableSummary { name: string; description?: string }
interface ColumnSummary { column_name: string; data_type?: string }

function cmpNoCase(a: string, b: string) {
  return a.toLowerCase().localeCompare(b.toLowerCase())
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
  marginBottom: 4, marginTop: 0,
}
const sectionTitleStyle: React.CSSProperties = {
  fontSize: '0.8625rem', fontWeight: 600, color: 'var(--color-accent)',
  marginTop: 14, marginBottom: 6, paddingBottom: 4,
  borderBottom: '1px solid var(--color-border)',
}
const helpStyle: React.CSSProperties = {
  fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4,
}
const errorStyle: React.CSSProperties = {
  fontSize: '0.8125rem', color: '#ef4444', marginTop: 4, fontWeight: 500,
}
const textInput: React.CSSProperties = {
  width: '100%', padding: '6px 8px', background: 'var(--color-input-bg)',
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  color: 'var(--color-text)', fontSize: '0.8625rem', boxSizing: 'border-box',
}

export default function LookupConfigDialog({
  name, subjectKind = 'column', value, onClose, onSave, onClear,
}: LookupConfigDialogProps) {
  const project = getGenprocProject()

  const [table, setTable] = useState(value?.table ?? '')
  const [displayColumn, setDisplayColumn] = useState(value?.display_column ?? '')
  const [valueColumn, setValueColumn] = useState(value?.value_column ?? '')
  const [searchColumns, setSearchColumns] = useState((value?.search_columns ?? []).join(', '))
  const [filter, setFilter] = useState(value?.filter ?? '')

  const [tables, setTables] = useState<TableSummary[]>([])
  const [cols, setCols] = useState<ColumnSummary[]>([])
  const [colsLoading, setColsLoading] = useState(false)

  const tableOptions = useMemo<ComboboxOption[]>(
    () => tables.map((t) => ({ value: t.name, hint: t.description })), [tables])
  const colOptions = useMemo<ComboboxOption[]>(
    () => cols.map((c) => ({ value: c.column_name, hint: c.data_type })), [cols])

  // Load project tables (preview-schema ∪ Database Designer), like the txn editor.
  useEffect(() => {
    if (!project) return
    let cancelled = false
    Promise.allSettled([livePreviewTables(project), getTables(project)])
      .then(([preview, design]) => {
        if (cancelled) return
        const byKey = new Map<string, TableSummary>()
        const add = (n?: string, description?: string) => {
          const nm = (n || '').trim()
          if (!nm) return
          const key = nm.toLowerCase()
          if (!byKey.has(key)) byKey.set(key, { name: nm, description })
          else if (description && !byKey.get(key)!.description) {
            byKey.set(key, { name: byKey.get(key)!.name, description })
          }
        }
        if (preview.status === 'fulfilled') {
          for (const t of preview.value?.tables || []) add(t.name)
        }
        if (design.status === 'fulfilled') {
          const list = Array.isArray(design.value?.tables) ? design.value.tables : []
          for (const t of list) add(t.name, t.description)
        }
        setTables([...byKey.values()].sort((a, b) => cmpNoCase(a.name, b.name)))
      })
      .catch(() => { if (!cancelled) setTables([]) })
    return () => { cancelled = true }
  }, [project])

  // Load the chosen table's columns (debounced) for the display/value pickers.
  useEffect(() => {
    const t = table.trim()
    if (!project || !t) { setCols([]); setColsLoading(false); return }
    let cancelled = false
    setColsLoading(true)
    const timer = window.setTimeout(() => {
      Promise.allSettled([livePreviewColumns(project, t), getTableInfo(project, t)])
        .then(([live, design]) => {
          if (cancelled) return
          const byKey = new Map<string, ColumnSummary>()
          const add = (n?: string, dataType?: string) => {
            const nm = (n || '').trim()
            if (!nm) return
            const key = nm.toLowerCase()
            if (!byKey.has(key)) byKey.set(key, { column_name: nm, data_type: dataType })
            else if (dataType && !byKey.get(key)!.data_type) {
              byKey.set(key, { column_name: byKey.get(key)!.column_name, data_type: dataType })
            }
          }
          if (live.status === 'fulfilled') {
            for (const c of live.value?.columns || []) add(c.column_name, c.data_type)
          }
          if (design.status === 'fulfilled') {
            const list = Array.isArray(design.value?.table?.columns) ? design.value.table.columns : []
            for (const c of list) add(c.column_name, c.data_type)
          }
          setCols([...byKey.values()])
        })
        .catch(() => { if (!cancelled) setCols([]) })
        .finally(() => { if (!cancelled) setColsLoading(false) })
    }, 250)
    return () => { cancelled = true; window.clearTimeout(timer) }
  }, [project, table])

  const errTable = !table.trim()
  const errValue = !valueColumn.trim()
  const canSave = !errTable && !errValue

  const handleSave = () => {
    if (!canSave) return
    const extra = searchColumns.split(',').map((s) => s.trim()).filter(Boolean)
    onSave({
      table: table.trim(),
      value_column: valueColumn.trim(),
      ...(displayColumn.trim() ? { display_column: displayColumn.trim() } : {}),
      ...(extra.length ? { search_columns: extra } : {}),
      ...(filter.trim() ? { filter: filter.trim() } : {}),
    })
  }

  const intro = subjectKind === 'criterion'
    ? <>This criterion shows a 🔍 picker that searches the table below (in the
        process&apos;s target database). The chosen <strong>value column</strong>{' '}
        becomes the criterion value.</>
    : <>On the result grid, editable cells of this column show a 🔍 picker that
        searches the table below. The chosen <strong>value column</strong> is
        stored into the cell.</>

  const placeholderToken = subjectKind === 'criterion' ? '{another_criterion}' : '{column}'
  const filterHelp = subjectKind === 'criterion'
    ? <>SQL WHERE-fragment on the reference table. Use <code>{'{name}'}</code> to
        insert another criterion&apos;s current value — it expands to an escaped
        literal, so don&apos;t add quotes.</>
    : <>SQL WHERE-fragment on the reference table. Use <code>{'{column}'}</code>{' '}
        to insert a value from the row being edited — it expands to an escaped
        literal, so don&apos;t add quotes.</>

  return (
    <Modal
      open
      onClose={onClose}
      title={`Lookup for "${name}"`}
      size="wide"
      footer={
        <div className="flex items-center justify-between gap-2" style={{ width: '100%' }}>
          <button
            className="btn btn-ghost btn-sm"
            style={{ color: value ? '#ef4444' : 'var(--color-text-secondary)', visibility: value ? 'visible' : 'hidden' }}
            onClick={onClear}
          >
            Remove lookup
          </button>
          <div className="flex items-center gap-2">
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={!canSave} onClick={handleSave}>
              Save Lookup
            </button>
          </div>
        </div>
      }
    >
      <div>
        <div style={{ ...helpStyle, marginTop: 0, marginBottom: 8 }}>{intro}</div>

        <div style={{ ...sectionTitleStyle, marginTop: 0 }}>1. Source table</div>
        <Combobox
          value={table}
          onChange={setTable}
          options={tableOptions}
          placeholder="Type or pick a table — e.g. REGION_MASTER"
          invalid={errTable}
          ariaLabel="Reference table"
          emptyText="No tables found — type a table name."
        />
        <div style={helpStyle}>
          Type to search, or click the ▾ to browse every table in this project.
        </div>
        {errTable && <div style={errorStyle}>Pick a reference table.</div>}

        <div style={sectionTitleStyle}>2. Columns</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Display column (what the user sees)</label>
            <Combobox
              value={displayColumn}
              onChange={setDisplayColumn}
              options={colOptions}
              loading={colsLoading}
              placeholder="e.g. region_name (defaults to the value column)"
              ariaLabel="Display column"
              emptyText={table.trim() ? 'No columns found — type a name.' : 'Pick a table first.'}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Value column (what gets stored)</label>
            <Combobox
              value={valueColumn}
              onChange={setValueColumn}
              options={colOptions}
              loading={colsLoading}
              placeholder="e.g. region_code"
              invalid={errValue}
              ariaLabel="Value column"
              emptyText={table.trim() ? 'No columns found — type a name.' : 'Pick a table first.'}
            />
            {errValue && <div style={errorStyle}>Pick the value (stored) column.</div>}
          </div>
        </div>

        <div style={sectionTitleStyle}>3. Search &amp; filter (optional)</div>
        <label style={labelStyle}>Extra search columns (comma-separated)</label>
        <input
          type="text"
          value={searchColumns}
          onChange={(e) => setSearchColumns(e.target.value)}
          placeholder="region_name, abbreviation"
          aria-label="Extra search columns"
          style={textInput}
        />
        <div style={helpStyle}>
          The display and value columns are always searched; add more here to
          widen the &ldquo;type to filter&rdquo; match.
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginTop: 12 }}>
          <label style={{ ...labelStyle, marginTop: 0, flex: 1 }}>Filter</label>
          <SqlBuilderButton value={filter} onChange={setFilter} mode="where" title="Lookup filter" project={project || undefined}
            contextTables={table.trim() ? [table.trim()] : undefined} />
        </div>
        <textarea
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={`active = true AND country = ${placeholderToken}`}
          aria-label="Lookup filter"
          rows={2}
          style={{ ...textInput, fontFamily: 'monospace', resize: 'vertical' }}
        />
        <div style={helpStyle}>{filterHelp}</div>
      </div>
    </Modal>
  )
}
